import { useEffect, useState } from 'react';

interface PadReading {
  id: string;
  standard: boolean;
  pressed: number[];
  axes: number[];
}

/** The standard mapping's names for the button places, so a reading can be checked against the rules. */
const NAMES: Record<number, string> = {
  0: 'bottom face',
  1: 'right face',
  2: 'left face',
  3: 'top face',
  4: 'LB',
  5: 'RB',
  6: 'LT',
  7: 'RT',
  8: 'Back',
  9: 'Start',
  10: 'left stick click',
  11: 'right stick click',
  12: 'D-pad up',
  13: 'D-pad down',
  14: 'D-pad left',
  15: 'D-pad right',
  16: 'Guide',
};

function readPads(): PadReading[] {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return [];
  const readings: PadReading[] = [];
  for (const pad of navigator.getGamepads()) {
    if (!pad?.connected) continue;
    readings.push({
      id: pad.id,
      standard: pad.mapping === 'standard',
      pressed: pad.buttons.flatMap((b, i) => (b.pressed || b.value > 0.3 ? [i] : [])),
      axes: pad.axes.map((a) => Math.round(a * 100) / 100),
    });
  }
  return readings;
}

/**
 * What the browser sees of a game controller, live: which pad, whether it reports the standard
 * layout, the buttons down and the sticks. A player whose pad does nothing can tell at once
 * whether the browser has woken it (a pad is hidden until a button is pressed) and whether its
 * mode switch gives the standard layout, which is the one the rules describe.
 */
export function GamepadMonitor() {
  const [pads, setPads] = useState<PadReading[]>([]);
  useEffect(() => {
    let frame = 0;
    const poll = () => {
      const next = readPads();
      setPads((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      frame = requestAnimationFrame(poll);
    };
    frame = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <div className="gamepad-monitor" data-testid="gamepad-monitor" aria-live="polite">
      {pads.length === 0 ? (
        <p className="hint">
          No controller seen yet. Plug one in or pair it, then press any button: the browser keeps a
          pad hidden until it is used.
        </p>
      ) : (
        pads.map((pad) => (
          <div key={pad.id} className="gamepad-reading">
            <p className="menu-text">
              <strong>{pad.id}</strong>
              {pad.standard ? '' : ' · not the standard layout: check the pad’s mode switch'}
            </p>
            <p className="hint">
              Buttons:{' '}
              {pad.pressed.length === 0
                ? 'none'
                : pad.pressed.map((i) => `${i} ${NAMES[i] ?? ''}`.trim()).join(', ')}
              <br />
              Sticks: {pad.axes.join(', ')}
            </p>
          </div>
        ))
      )}
    </div>
  );
}
